import type { InSaleAndStopSellingItemsType } from "@/components/features/items/stock/_shared";

import { isPseudoPlanID } from "./is-pseudo-plan-id";

import type { PlanRoomDailyRateRow } from "./types";

type NamedInSaleItem = {
	id: string;
	name: string;
	inSale: boolean;
};

export type DisplayFilterItems = {
	inSaleItems: InSaleAndStopSellingItemsType;
	stopSellingItems: InSaleAndStopSellingItemsType;
};

const toFilterItem = (item: NamedInSaleItem) => ({ id: item.id, name: item.name });

const isInSaleLabel = (salePeriodLabel: string): boolean => salePeriodLabel === "販売中";

/**
 * 在庫ページと同じ分割: 販売中 / 非公開。
 */
export const splitByInSale = (items: NamedInSaleItem[]): DisplayFilterItems => ({
	inSaleItems: items.filter((item) => item.inSale).map(toFilterItem),
	stopSellingItems: items.filter((item) => !item.inSale).map(toFilterItem),
});

/**
 * 在庫と同じ初期選択。
 * モバイル（単一セレクト）は販売中の先頭、なければ停止中の先頭。
 * PC は販売中が 15 件以上なら先頭のみ、未満なら販売中すべて。
 */
export const getInitialSelectedItemIDs = (
	items: DisplayFilterItems,
	options?: { single: boolean },
): string[] => {
	if (options?.single) {
		const first = items.inSaleItems.at(0)?.id ?? items.stopSellingItems.at(0)?.id;
		return first == null ? [] : [first];
	}
	if (items.inSaleItems.length >= 15) {
		return [items.inSaleItems.at(0)?.id || ""];
	}
	return items.inSaleItems.map((item) => item.id);
};

/**
 * デスクトップで複数選んだあとにモバイル幅へ落ちたとき、セレクトとテーブルを一件に揃える。
 */
export const toSingleSelectedIDs = (ids: string[]): string[] =>
	ids.length > 1 ? ids.slice(0, 1) : ids;

/**
 * プラン表示制御。通常プランは plan.inSale 由来の salePeriodLabel を使う（部屋ごとで同じ）。
 * 擬似プランは複数部屋で同じ planID を共有するので、どれか一室でも販売中なら販売中にする。
 */
export const buildPlanDisplayFilterItems = (rows: PlanRoomDailyRateRow[]): DisplayFilterItems => {
	const plans = new Map<string, NamedInSaleItem>();

	for (const row of rows) {
		const inSale = isInSaleLabel(row.salePeriodLabel);
		const existing = plans.get(row.planID);
		if (existing == null) {
			plans.set(row.planID, {
				id: row.planID,
				name: row.planName,
				inSale,
			});
			continue;
		}
		if (isPseudoPlanID(row.planID) && inSale) {
			existing.inSale = true;
		}
	}

	return splitByInSale([...plans.values()]);
};

export type RoomDisplayFilterSource = {
	roomID: string;
	roomName: string;
	inSale: boolean;
};

/**
 * 部屋表示制御。在庫ページと同じく部屋マスタの公開状態で分割する。
 * プラン行の salePeriodLabel は部屋の公開状態ではないので使わない。
 * 料金行の有無は見ない。販売中なのに料金が無い部屋もここに出す。
 * ダミー部屋は呼び出し側で、プラン行に出たものだけ渡す。
 */
export const buildRoomDisplayFilterItems = (rooms: RoomDisplayFilterSource[]): DisplayFilterItems =>
	splitByInSale(
		rooms.map((room) => ({
			id: room.roomID,
			name: room.roomName,
			inSale: room.inSale,
		})),
	);
